import React, {useMemo} from "react";
import {Box, Stack} from "@unibuddy/patron";
import {format} from "date-fns";
import {MessageBubbleDesktop, MessageRow, getMessageSequencePosition} from "@unibuddy/chat-ui";

import {
    AmbassadorProfileInformation,
    AssistantConversationMessage,
    Maybe,
    SenderRole,
    StaffProfileInformation,
} from "../../../../types/generated";
import {ChatAmbassadorProfile} from "../ChatAmbassadorProfile";
import {ChatMessageText} from "./ChatMessageText";
import {ChatMessageFeedback} from "./ChatMessageFeedback";
import {assistantMessageGetProfile} from "../../utils/assistantMessage";
import {
    ConversationState,
    useAssistantConversationContext,
} from "../../providers/AssistantConversationProvider";
import {ChatMessageHeader} from "./ChatMessageHeader";

export type Message = {
    sender: {
        id: string;
    };
};

export type Align = "left" | "right" | "center";
export type Position = "top" | "middle" | "bottom" | "orphan";

type ChatMessageProps = {
    message: AssistantConversationMessage;
    index: number;
    array: AssistantConversationMessage[];
};

type ChatBubbleProps = {
    align: Align;
    position: Position;
    message: AssistantConversationMessage;
    isLast: boolean;
    isFirst: boolean;
};

const ASSISTANT_USER = "assistant";

const restructureMessageType = (message: AssistantConversationMessage): Message => {
    return {
        sender: {
            id: message.sender,
        },
    } as Message;
};

const getMessagePosition = (index: number, array: AssistantConversationMessage[]) => {
    return getMessageSequencePosition({
        currentMessage: restructureMessageType(array?.[index]),
        nextMessage:
            index === array.length - 1 ? undefined : restructureMessageType(array?.[index + 1]),
        previousMessage: index === 0 ? undefined : restructureMessageType(array?.[index - 1]),
    });
};

function ChatBubble({align, position, message, isLast, isFirst}: Readonly<ChatBubbleProps>) {
    const {conversationState} = useAssistantConversationContext();
    const messageHasFeedback = useMemo(() => !!message.feedback, [message]);
    const messageCanShowFeedback = useMemo(
        () =>
            conversationState === ConversationState.USER_CAN_SEND_MESSAGE &&
            isLast &&
            !isFirst &&
            message.sender === SenderRole.Assistant,
        [message, isLast, isFirst, conversationState],
    );

    const showMessageFeedback = messageHasFeedback || messageCanShowFeedback;

    return (
        <MessageBubbleDesktop align={align} position={position}>
            <ChatMessageText align={align} message={message} isLast={isLast} />
            {showMessageFeedback && (
                <ChatMessageFeedback
                    messageId={message.id}
                    messageFeedback={message?.feedback ?? null}
                />
            )}
        </MessageBubbleDesktop>
    );
}

function ChatBubbleWithMetadata({
    align,
    position,
    message,
    isLast,
    isFirst,
}: Readonly<ChatBubbleProps>) {
    const assistantMessageProfile = useMemo(() => {
        return assistantMessageGetProfile(message) as Maybe<
            AmbassadorProfileInformation | StaffProfileInformation
        >;
    }, [message]);

    if (assistantMessageProfile) {
        return (
            <Stack space="small">
                <ChatBubble
                    align={align}
                    position={position}
                    message={message}
                    isLast={isLast}
                    isFirst={isFirst}
                />
                <ChatAmbassadorProfile metadata={assistantMessageProfile} />
            </Stack>
        );
    }

    return (
        // Adding empty Box as bubble styles between 420-450px are bad
        <Box display="flex" flexDirection="row">
            {align === "right" && (
                <Box display={["none", "block"]} w="30px" h="1px" flexShrink={0} />
            )}
            <ChatBubble
                align={align}
                position={position}
                message={message}
                isLast={isLast}
                isFirst={isFirst}
            />
            {align === "left" && (
                <Box display={["none", "block"]} w="30px" h="1px" flexShrink={0} />
            )}
        </Box>
    );
}

export function ChatMessage({message, index, array}: Readonly<ChatMessageProps>) {
    const formattedTime = message.createdAt ? format(new Date(message.createdAt), "k:mm a") : null;
    const position = getMessagePosition(index, array);
    const align = message.sender.toLowerCase() === ASSISTANT_USER.toLowerCase() ? "left" : "right";

    const isFirst = index === 0;
    const isLast = index === array.length - 1;

    return (
        <Box key={index} paddingX="xsmall" role="listitem" aria-live="polite" aria-atomic="true">
            <MessageRow
                align={align}
                position={position}
                addonTop={
                    (position === "top" || position === "orphan") && (
                        <ChatMessageHeader align={align} formattedTime={formattedTime} />
                    )
                }
            >
                <ChatBubbleWithMetadata
                    isFirst={isFirst}
                    isLast={isLast}
                    align={align}
                    position={position}
                    message={message}
                />
            </MessageRow>
        </Box>
    );
}
